import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProcessingStore } from '../../stores/processingStore';
import { ArrowPathIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

export const ProcessingIndicator: React.FC = () => {
  const navigate = useNavigate();
  const { activeProcess, checkActiveProcesses } = useProcessingStore();

  useEffect(() => {
    // Check for active processes when component mounts
    checkActiveProcesses();
  }, [checkActiveProcesses]);

  if (!activeProcess) {
    return null;
  }

  const getStatusIcon = () => {
    if (activeProcess.stage === 'completed') {
      return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
    }
    if (activeProcess.stage === 'failed') {
      return <ExclamationCircleIcon className="w-5 h-5 text-red-600" />;
    }
    return <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />;
  };

  const getStatusColor = () => {
    if (activeProcess.stage === 'completed') {
      return 'bg-green-100 border-green-200 text-green-800';
    }
    if (activeProcess.stage === 'failed') {
      return 'bg-red-100 border-red-200 text-red-800';
    }
    return 'bg-blue-100 border-blue-200 text-blue-800';
  };

  const formatStage = (stage: string) => {
    return stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleClick = () => {
    // Navigate to results page for the active process
    if (activeProcess.fileId) {
      navigate(`/results?file=${activeProcess.fileId}`);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={clsx(
        'flex items-center gap-3 px-4 py-2 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity',
        getStatusColor()
      )}
    >
      {getStatusIcon()}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">
          Processing: {activeProcess.filename}
        </div>
        <div className="text-xs opacity-75">
          {formatStage(activeProcess.stage)} • {activeProcess.progress}%
        </div>
      </div>
    </div>
  );
};